import { api, throwApiError } from "../../../api/api";
import type { DpPaginated } from "./dpTypes";
import type { DpPayrollPeriodStatus, DpPayrollResultLine, DpPayrollResultMeta, DpPayrollResultStatus } from "./payrollApi";
import type { DpFinanceView } from "./financeBridge";

export type DpAccrualStatus =
  | "EM_AQUISICAO"
  | "DISPONIVEL"
  | "PARCIALMENTE_GOZADO"
  | "GOZADO"
  | "VENCIDO"
  | "PERDIDO";

export const ACCRUAL_STATUS_LABELS: Record<DpAccrualStatus, string> = {
  EM_AQUISICAO: "Em aquisição",
  DISPONIVEL: "Disponível",
  PARCIALMENTE_GOZADO: "Programado/parcial",
  GOZADO: "Gozado",
  VENCIDO: "Vencido",
  PERDIDO: "Perdido",
};

export const ACCRUAL_STATUS_BADGE: Record<DpAccrualStatus, string> = {
  EM_AQUISICAO: "dpBadge--gray",
  DISPONIVEL: "dpBadge--green",
  PARCIALMENTE_GOZADO: "dpBadge--blue",
  GOZADO: "dpBadge--gray",
  VENCIDO: "dpBadge--red",
  PERDIDO: "dpBadge--gray",
};

export type DpVacationStatus = "PLANEJADO" | "AVISADO" | "EM_GOZO" | "CONCLUIDO" | "CANCELADO";

export const VACATION_STATUS_LABELS: Record<DpVacationStatus, string> = {
  PLANEJADO: "Planejado",
  AVISADO: "Avisado",
  EM_GOZO: "Em gozo",
  CONCLUIDO: "Concluído",
  CANCELADO: "Cancelado",
};

export const VACATION_STATUS_BADGE: Record<DpVacationStatus, string> = {
  PLANEJADO: "dpBadge--orange",
  AVISADO: "dpBadge--blue",
  EM_GOZO: "dpBadge--green",
  CONCLUIDO: "dpBadge--gray",
  CANCELADO: "dpBadge--gray",
};

export type DpVacationSchedule = {
  id: string;
  accrualPeriodId: string;
  employeeId: string;
  startDate: string;
  endDate: string;
  days: number;
  abonoDays: number;
  thirteenthAdvance: boolean;
  noticeIssuedAt: string | null;
  noticeFileName: string | null;
  hasNoticeFile: boolean;
  noticeDeadline: string;
  daysToStart: number;
  status: DpVacationStatus;
  canceledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  createdBy: { id: string; nomeCompleto: string };
  /** Recibo de férias (F2b): null = ainda não calculado. */
  payment: DpVacationPaymentSummary | null;
};

export type DpVacationPaymentSummary = {
  status: DpPayrollPeriodStatus;
  resultStatus: DpPayrollResultStatus;
  netAmount: string;
  paymentDate: string;
};

/** Selo do recibo na ficha e na visão de férias. */
export const vacationPaymentBadge = (payment: DpVacationPaymentSummary | null): { label: string; className: string } =>
  !payment
    ? { label: "Não calculado", className: "dpBadge--gray" }
    : payment.status === "FECHADO"
      ? { label: "Fechado", className: "dpBadge--green" }
      : payment.resultStatus === "PENDENTE"
        ? { label: "Aberto · pendente", className: "dpBadge--orange" }
        : { label: "Aberto", className: "dpBadge--blue" };

/** O recibo existe a partir do aviso (arts. 135 e 145). */
export const hasVacationPayment = (schedule: Pick<DpVacationSchedule, "status">) =>
  schedule.status === "AVISADO" || schedule.status === "EM_GOZO" || schedule.status === "CONCLUIDO";

/** Recibo de férias gravado, ou a prévia calculada na hora (`saved: false`). */
export type DpVacationPayment = {
  saved: boolean;
  /** null = prévia, ainda não gravada. */
  status: DpPayrollPeriodStatus | null;
  resultStatus: DpPayrollResultStatus;
  paymentDate: string;
  /** Só na prévia: início − 2 dias, no dia útil anterior. */
  suggestedPaymentDate?: string;
  schedule: {
    id: string;
    status: DpVacationStatus;
    startDate: string;
    endDate: string;
    days: number;
    abonoDays: number;
    thirteenthAdvance: boolean;
    noticeIssuedAt: string | null;
    acquisitionStart: string;
    acquisitionEnd: string;
  } | null;
  meta: DpPayrollResultMeta;
  grossAmount: string;
  deductionsAmount: string;
  netAmount: string;
  inssBase: string;
  inssAmount: string;
  irrfBase: string;
  irrfAmount: string;
  fgtsBase: string;
  fgtsAmount: string;
  monthParts: Array<{ year: number; month: number; days: number; vacationAmount: string; thirdAmount: string; inssAmount: string }>;
  warnings: string[];
  /** Título do líquido no Financeiro (ADR 0029); null na prévia. */
  financeiro: DpFinanceView | null;
  lines: Array<Omit<DpPayrollResultLine, "order">>;
  calculatedAt: string | null;
  closedAt: string | null;
  closedBy: { id: string; nomeCompleto: string } | null;
  reopenedAt: string | null;
  reopenedBy: { id: string; nomeCompleto: string } | null;
  reopenReason: string | null;
};

export type DpAccrualPeriod = {
  id: string;
  contractId: string;
  sequence: number;
  acquisitionStart: string;
  acquisitionEnd: string;
  concessionEnd: string;
  entitledDays: number;
  unjustifiedAbsences: number;
  lostByLeave: boolean;
  /** Art. 133 §2: dia do retorno em que começa a nova contagem (só no período perdido; null sem retorno). */
  restartsOn: string | null;
  notes: string | null;
  status: DpAccrualStatus;
  balance: number;
  /** 0 quando o abono do período já foi lançado. */
  maxAbonoDays: number;
  daysToConcessionEnd: number;
  alerts: string[];
  schedules: DpVacationSchedule[];
};

export type DpExpiringPeriod = DpAccrualPeriod & {
  employee: { id: string; fullName: string; socialName: string | null; status: string };
};

export type DpCalendarItem = DpVacationSchedule & {
  employee: { id: string; fullName: string; socialName: string | null };
};

export type ScheduleVacationPayload = {
  startDate: string;
  endDate: string;
  abonoDays?: number;
  thirteenthAdvance?: boolean;
};

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

export const listEmployeeVacationsApi = async (token: string, employeeId: string): Promise<DpAccrualPeriod[]> => {
  try {
    const { data } = await api.get<{ periods: DpAccrualPeriod[] }>(`/dp/employees/${employeeId}/vacations`, auth(token));
    return data.periods;
  } catch (error) {
    throwApiError(error, "Erro ao carregar as férias.");
    throw error;
  }
};

export const updateVacationEntitlementApi = async (
  token: string,
  periodId: string,
  payload: { unjustifiedAbsences: number; notes?: string | null }
): Promise<DpAccrualPeriod> => {
  try {
    const { data } = await api.patch<DpAccrualPeriod>(`/dp/vacation-periods/${periodId}/entitlement`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao salvar as faltas do período.");
    throw error;
  }
};

/** Erros de regra voltam como lista (400); avisos voltam em `warnings` no sucesso. */
export const scheduleVacationApi = async (
  token: string,
  periodId: string,
  payload: ScheduleVacationPayload
): Promise<{ schedule: DpVacationSchedule; warnings: string[] }> => {
  try {
    const { data } = await api.post<{ schedule: DpVacationSchedule; warnings: string[] }>(
      `/dp/vacation-periods/${periodId}/schedules`,
      payload,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao programar as férias.");
    throw error;
  }
};

export const uploadVacationFileApi = async (
  token: string,
  file: File
): Promise<{ assetId: string; fileName: string | null }> => {
  const form = new FormData();
  form.append("file", file);
  try {
    const { data } = await api.post<{ assetId: string; fileName: string | null }>("/dp/vacations/files", form, {
      headers: { ...auth(token).headers, "Content-Type": "multipart/form-data" },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao enviar o arquivo.");
    throw error;
  }
};

export const issueVacationNoticeApi = async (
  token: string,
  scheduleId: string,
  payload: { noticeIssuedAt: string; noticeAssetId?: string | null; noticeFileName?: string | null }
): Promise<{ schedule: DpVacationSchedule; warnings: string[] }> => {
  try {
    const { data } = await api.post<{ schedule: DpVacationSchedule; warnings: string[] }>(
      `/dp/vacation-schedules/${scheduleId}/notice`,
      payload,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao registrar o aviso.");
    throw error;
  }
};

export const cancelVacationScheduleApi = async (token: string, scheduleId: string, reason: string) => {
  try {
    // financeiroWarnings: título do recibo com baixa no Financeiro, que continua lá (ADR 0029).
    const { data } = await api.post<DpVacationSchedule & { financeiroWarnings?: string[] }>(
      `/dp/vacation-schedules/${scheduleId}/cancel`,
      { reason },
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao cancelar as férias.");
    throw error;
  }
};

export const getVacationNoticeBlobApi = async (token: string, scheduleId: string): Promise<Blob> => {
  try {
    const { data } = await api.get<Blob>(`/dp/vacation-schedules/${scheduleId}/notice-file`, {
      ...auth(token),
      responseType: "blob",
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao baixar o aviso.");
    throw error;
  }
};

export const listExpiringVacationsApi = async (
  token: string,
  params: { page?: number; limit?: number; days?: number; costCenterId?: string }
): Promise<DpPaginated<DpExpiringPeriod>> => {
  try {
    const { data } = await api.get<DpPaginated<DpExpiringPeriod>>("/dp/vacations/expiring", { ...auth(token), params });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar os períodos a vencer.");
    throw error;
  }
};

export const getVacationsSummaryApi = async (token: string): Promise<{ expired: number; expiring: number }> => {
  try {
    const { data } = await api.get<{ expired: number; expiring: number }>("/dp/vacations/summary", auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao consultar as férias.");
    throw error;
  }
};

// ── Recibo de férias (F2b) ──────────────────────────────────────────────────

const paymentCall = async (fallback: string, run: () => Promise<{ data: DpVacationPayment }>) => {
  try {
    const { data } = await run();
    return data;
  } catch (error) {
    throwApiError(error, fallback);
    throw error;
  }
};

const paymentUrl = (scheduleId: string, suffix = "") => `/dp/vacation-schedules/${scheduleId}/payment${suffix}`;

export const getVacationPaymentApi = (token: string, scheduleId: string) =>
  paymentCall("Erro ao carregar o recibo de férias.", () => api.get(paymentUrl(scheduleId), auth(token)));

export const updateVacationPaymentApi = (token: string, scheduleId: string, paymentDate: string) =>
  paymentCall("Erro ao alterar a data de pagamento.", () => api.patch(paymentUrl(scheduleId), { paymentDate }, auth(token)));

export const calculateVacationPaymentApi = (token: string, scheduleId: string) =>
  paymentCall("Erro ao calcular o recibo.", () => api.post(paymentUrl(scheduleId, "/calculate"), {}, auth(token)));

export const closeVacationPaymentApi = (token: string, scheduleId: string) =>
  paymentCall("Erro ao fechar o recibo.", () => api.post(paymentUrl(scheduleId, "/close"), {}, auth(token)));

export const reopenVacationPaymentApi = (token: string, scheduleId: string, reason: string) =>
  paymentCall("Erro ao reabrir o recibo.", () => api.post(paymentUrl(scheduleId, "/reopen"), { reason }, auth(token)));

/**
 * "Aviso e recibo de férias — conferência" em PDF. Como no contracheque, o erro do
 * backend (404 não calculado, 409 pendente) chega como blob e é lido aqui.
 */
export const getVacationPaymentPdfApi = async (token: string, scheduleId: string): Promise<Blob> => {
  try {
    const { data } = await api.get<Blob>(paymentUrl(scheduleId, "/pdf"), { ...auth(token), responseType: "blob" });
    return data;
  } catch (error: any) {
    const body = error?.response?.data;
    if (body instanceof Blob) {
      let message = "Erro ao gerar o recibo de férias.";
      try {
        const parsed = JSON.parse(await body.text());
        if (typeof parsed?.message === "string") message = parsed.message;
      } catch {
        // corpo não é JSON: fica a mensagem padrão
      }
      throw new Error(message);
    }
    throwApiError(error, "Erro ao gerar o recibo de férias.");
    throw error;
  }
};

export const getVacationCalendarApi = async (
  token: string,
  params: { from: string; to: string; costCenterId?: string }
): Promise<DpCalendarItem[]> => {
  try {
    const { data } = await api.get<{ items: DpCalendarItem[] }>("/dp/vacations/calendar", { ...auth(token), params });
    return data.items;
  } catch (error) {
    throwApiError(error, "Erro ao carregar as próximas férias.");
    throw error;
  }
};
